import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { NestedTreeControl } from '@angular/cdk/tree';
import { Location } from '@angular/common';
import {
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgControl, NgForm } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { format, startOfDay } from 'date-fns';
import { cloneDeep } from 'lodash';
import { MeteorObservable } from 'meteor-rxjs';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  of,
  pairwise,
  shareReplay,
  startWith,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { getWriteDepartmentIds } from 'server/helpers/permissions/departmentPermissions';
import {
  getDefaultDispoPermissions,
  makePermissionsComplete,
} from 'server/helpers/permissions/permissions';
import type { UpdateProfileArgs } from 'server/methods/updateProfileArgsSchema';
import { Customers } from 'server/shared/collections/customers';
import { DocumentUploads } from 'server/shared/collections/documentUploads';
import { OptIns } from 'server/shared/collections/optIns';
import { Organisations } from 'server/shared/collections/organisations';
import { Users } from 'server/shared/collections/users';
import { getCountDown } from 'server/shared/helpers/getCountDown';
import { removeTimeZoneOffset } from 'server/shared/helpers/removeTimeZoneOffset';
import { loadTemplateTranslation } from 'server/shared/helpers/translations/translations';
import { trimPhone } from 'server/shared/helpers/trimPhoneHelper';
import { CarLicenseEnum } from 'server/shared/models/carLicense.enum';
import { Certificate } from 'server/shared/models/certificate';
import {
  CustomCriteria,
  CustomCriteriaOnJobSelect,
  CustomCriteriaOnJobToggle,
  CustomCriteriaType,
} from 'server/shared/models/customCriteria';
import { Customer, CustomerTypeEnum } from 'server/shared/models/customer';
import { Department } from 'server/shared/models/department';
import {
  DepartmentPermissions,
  DispoPermission,
  DispoPermissions,
} from 'server/shared/models/dispoPermissions';
import { DocumentUpload } from 'server/shared/models/documentUpload';
import { InpoolLanguage } from 'server/shared/models/InpoolLanguage';
import { isInputValidationError } from 'server/shared/models/inputValidationError';
import { InviteeFile } from 'server/shared/models/inviteeFile';
import { JobTypeEnum } from 'server/shared/models/jobTypeEnum';
import { LanguageEnum } from 'server/shared/models/language.enum';
import { NotificationEnum } from 'server/shared/models/notificationEnum';
import { OptIn } from 'server/shared/models/optIn';
import { Organisation } from 'server/shared/models/organisation';
import { RecipientEnum } from 'server/shared/models/recipientEnum';
import { RoleEnum } from 'server/shared/models/role.enum';
import { SexEnum } from 'server/shared/models/sex.enum';
import { ShiftEnum } from 'server/shared/models/shift.enum';
import { Speciality } from 'server/shared/models/speciality';
import { Tag } from 'server/shared/models/tag';
import {
  BaseUser,
  DispoUser,
  PoolerUser,
  RequesterUser,
  User,
} from 'server/shared/models/user';
import { UserCustomerInformation } from 'server/shared/models/userCustomerInformation';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { CustomerService } from 'src/app/services/customer.service';
import { OrganisationService } from 'src/app/services/organisation.service';
import { UserService } from 'src/app/services/user.service';
import { logInputValidationError } from 'src/helpers/logInputValidationError';
import { observeTracker } from 'src/helpers/meteorObservable/observeTracker';
import { queryParam$ } from 'src/helpers/queryParam$';
import {
  DepartmentChangesDto,
  DepartmentMultiSelectMode,
} from '../../../../../server/shared/helpers/departmentMultiselect';
import { AccountService } from '../../services/account.service';
import { PermissionsService } from '../../services/permissions.service';
import { ToastService } from '../../services/toast/toast.service';
import { UnsavedChangesDeactivate } from '../../shared/guards/unsaved-changes-deactivate.interface';
import { SubscriptionService } from '../../shared/subscription.service';

const departmentMultiselectModeForRole: {
  [loggedInUserRole: string]:
    | { [profileUserRole: string]: DepartmentMultiSelectMode | undefined }
    | undefined;
} = {
  // logged in user role
  [RoleEnum.DISPO]: {
    // profile user role
    [RoleEnum.POOL]: 'pooler-assignment',
    [RoleEnum.REQUESTER]: 'edit-read-write',
    [RoleEnum.DISPO]: 'edit-read-write',
  },
  [RoleEnum.POOL]: {
    [RoleEnum.POOL]: 'edit-blacklist-inverted',
  },
};

export type UserWithCustomer = User & {
  customer: {
    id: string;
    displayName: string;
  };
};

@Component({
  selector: 'profile-edit',
  templateUrl: 'profile-edit.html',
  styleUrls: ['profile-edit.scss'],
})
export class ProfileEditComponent
  implements OnInit, OnDestroy, UnsavedChangesDeactivate
{
  destroyed$ = new Subject<void>();

  userIdParam$ = queryParam$(this.route, 'userId');

  editProfileUserId$ = this.userIdParam$.pipe(
    filter(Boolean),
    filter((id) => id !== 'new'),
    shareReplay(1),
  );

  profileUser?: User;
  profileUser$ = new BehaviorSubject<User | undefined>(undefined);
  editor?: User;
  loggedInUser?: User;
  userSubscription?: Subscription;
  documentUploadsSubscription?: Subscription;
  userOptInsSubscription?: Subscription;
  customerInformation?: UserCustomerInformation;
  poolerHourlyWages: { wage: number; customer: string }[] = [];
  poolerMonthlyHours: { hours: number; customer: string }[] = [];

  hasEditPermission = false;
  isSelfDispo = false;

  sexEnum = SexEnum;
  carLicenseEnum = CarLicenseEnum;
  shiftEnum = ShiftEnum;
  jobTypeEnum = JobTypeEnum;
  languageEnum = LanguageEnum;
  selectable = false;
  departmentSearch?: string;
  // @ts-ignore
  treeControl = new NestedTreeControl<Organisation>((node) => node.departments);
  organisations?: Organisation[];
  dispoCustomer?: Customer;
  poolerCustomers?: Customer[];
  poolerProfileCustomerForCriteria?: Customer;
  isNewUser: boolean = false;
  oldPhone?: string;
  oldEmail?: string;
  oldPushEnabled?: boolean;
  userId?: string;

  newHourlyWage?: number;
  newInternalId?: string;
  newPerigonId?: number;
  newNote?: string;
  newMonthlyHours?: number;

  isDispoEditingPooler: boolean = false;
  temporaryCvFiles: InviteeFile[] = [];

  partialOwnEditPermissions: boolean = false;

  isEditingRequestedUser$ = combineLatest([
    this.customerService.readyDispoCustomer$,
    this.userIdParam$,
  ]).pipe(
    map(([customer, userId]) => {
      return customer.pool.requestedUserIds?.includes(userId);
    }),
  );

  ccEmails?: string;

  readonly customCriteriaTypeEnum = CustomCriteriaType;

  customCriteriaCollapsed: boolean = true;

  uploadedFiles: DocumentUpload[] = [];
  @Input() isUploading = false;

  organisationsLoaded = new ReactiveVar(false);

  showDocumentsSubmitted: boolean = false;
  additionalInformationCollapsed: boolean = true;
  roleEnum = RoleEnum;
  editedUserRole: RoleEnum = RoleEnum.POOL;
  userWithCustomer?: UserWithCustomer;

  userOptIns: OptIn[] = [];
  phoneResetLockSeconds = 0;

  leaveDate?: string;
  joinDate?: string;
  datepickerJoin: Date = startOfDay(new Date());
  datepickerLeave: Date = startOfDay(new Date());

  datepickerDateOfBirth: Date = startOfDay(new Date());

  tags: Tag[] = [];

  creatorNotificationEnumArray: NotificationEnum[] = [];

  ownUserTracker?: Tracker.Computation;
  initUserTracker?: Tracker.Computation;
  profileUserTrackerId?: string;
  profileUserTracker?: Tracker.Computation;

  customerTracker?: Tracker.Computation;

  browserNotificationAllowed?: boolean = false;

  @ViewChild('languageInput', { static: false }) languageInput?: NgControl;
  @ViewChild('profileForm', { static: false }) form?: NgForm;
  @ViewChild('autosize') autosize?: CdkTextareaAutosize;

  readyUser$ = this.userService.readyUser$.pipe(shareReplay(1));
  customersOfUser$ = this.customerService.customersOfUser$;
  unsavedDepartmentChanges?: DepartmentChangesDto;

  creatorNotificationEnumArray$ = this.customerService.readyDispoCustomer$.pipe(
    map((customer) => {
      const creatorNotificationEnumArray: NotificationEnum[] = [];
      let templates = loadTemplateTranslation('de');
      Object.keys(templates.templates).forEach((key: string) => {
        if (
          // @ts-ignore
          templates.templates[key]?.[RecipientEnum.CREATOR]
        ) {
          if (customer.enableEmployments || !key.includes('EMPLOYMENT')) {
            creatorNotificationEnumArray.push(key as NotificationEnum);
          }
        }
      });
      return creatorNotificationEnumArray;
    }),
    takeUntil(this.destroyed$),
    shareReplay(1),
  );

  setLoggedInUserEffect$ = this.readyUser$.pipe(
    tap((user) => {
      this.setLoggedInUser(user);
    }),
    takeUntil(this.destroyed$),
  );
  poolerCustomersEffect$ = this.customerService.readyPoolerCustomers$.pipe(
    tap((customers) => {
      this.poolerCustomers = customers;
      this.resolvePoolerCustomCriteriaForSelfProfile();
    }),
    takeUntil(this.destroyed$),
  );
  organisationsEffect$ = this.subscriptionService.organisations.ready$.pipe(
    tap(() => {
      this.organisations = Organisations.find().fetch();
      this.organisationsLoaded.set(true);
    }),
    takeUntil(this.destroyed$),
  );
  dispoCustomerEffect$ = this.customerService.readyDispoCustomer$.pipe(
    tap((customer) => {
      this.dispoCustomer = customer;
      if (
        this.loggedInUser?.role === RoleEnum.DISPO &&
        this.profileUser?.role === RoleEnum.POOL &&
        this.loggedInUser.customerId &&
        this.customerInformation
      ) {
        this.mergeCustomerCustomCriteriaIntoInfo(
          this.customerInformation,
          customer,
        );
      }
    }),
    takeUntil(this.destroyed$),
  );
  userWithCustomerEffect$ = combineLatest([
    this.customerService.readyDispoCustomer$,
    this.readyUser$,
  ]).pipe(
    tap(([customer, user]) => {
      this.userWithCustomer = {
        ...user,
        customer: {
          id: this.dispoCustomer ? this.dispoCustomer._id : customer._id,
          displayName: this.dispoCustomer
            ? this.dispoCustomer.name
            : customer.name,
        },
      };
    }),
    takeUntil(this.destroyed$),
  );

  profileUserSubscription$ = this.editProfileUserId$.pipe(
    switchMap((userId) => {
      return combineLatest([
        MeteorObservable.subscribe('myProfile', userId),
        of(userId),
      ]).pipe(map(([_, userId]) => userId));
    }),
    takeUntil(this.destroyed$),
    shareReplay(1),
  );

  profileUserEffect$ = this.profileUserSubscription$.pipe(
    switchMap((userId) => {
      return observeTracker(() => Users.findOne(userId));
    }),
    tap((user) => {
      if (!user) return;
      if (user.role === RoleEnum.DISPO) {
        this.editedUserRole = RoleEnum.DISPO;
        this.isDispoEditingPooler = false;
      } else if (user.role === RoleEnum.REQUESTER) {
        this.editedUserRole = RoleEnum.REQUESTER;
        this.isDispoEditingPooler = false;
      } else {
        this.editedUserRole = RoleEnum.POOL;
        this.isDispoEditingPooler = true;
      }
    }),
    takeUntil(this.destroyed$),
  );

  optinsSubscription$ = this.editProfileUserId$.pipe(
    switchMap((userId) => {
      return combineLatest([
        MeteorObservable.subscribe('userOptIns', userId),
        of(userId),
      ]).pipe(map(([_, userId]) => userId));
    }),
    takeUntil(this.destroyed$),
    shareReplay(1),
  );

  optInsEffect$ = this.optinsSubscription$.pipe(
    switchMap((userId) => {
      return observeTracker(() => {
        this.userOptIns = OptIns.find({
          userId: userId,
        }).fetch();
      });
    }),
    takeUntil(this.destroyed$),
  );

  documentUploadsSubscription$ = this.editProfileUserId$.pipe(
    switchMap((userId) => {
      return combineLatest([
        MeteorObservable.subscribe('documentUploads', userId),
        of(userId),
      ]).pipe(map(([_, userId]) => userId));
    }),
    takeUntil(this.destroyed$),
    shareReplay(1),
  );

  documentUploadsEffect$ = this.documentUploadsSubscription$.pipe(
    switchMap((userId) => {
      return observeTracker(() => {
        this.uploadedFiles = DocumentUploads.find({
          userId: userId,
        }).fetch();
      });
    }),
    takeUntil(this.destroyed$),
  );

  isDemo$ = this.customerService.customersOfUser$.pipe(
    map((customers) => customers.some((customer) => customer.isDemo)),
    startWith(false),
    takeUntil(this.destroyed$),
    shareReplay(1),
  );

  initUserEffect$ = combineLatest([
    this.userIdParam$.pipe(startWith(undefined), pairwise()),
    this.readyUser$,
    this.organisationService.readyDepartments$,
    this.isDemo$,
  ]).pipe(
    tap(([[lastUserId, userId], readyUser, departments, isDemo]) => {
      if (!userId || userId !== lastUserId) {
        this._initUser(userId, readyUser, departments, isDemo);
      }
    }),
    takeUntil(this.destroyed$),
  );

  constructor(
    private ngZone: NgZone,
    private location: Location,
    private toastService: ToastService,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private subscriptionService: SubscriptionService,
    private organisationService: OrganisationService,
    private accountService: AccountService,
    public permissionsService: PermissionsService,
    private userService: UserService,
    private customerService: CustomerService,
  ) {
    this.setLoggedInUserEffect$.subscribe();
    this.poolerCustomersEffect$.subscribe();
    this.organisationsEffect$.subscribe();
    this.dispoCustomerEffect$.subscribe();
    this.userWithCustomerEffect$.subscribe();
    this.optInsEffect$.subscribe();
    this.profileUserEffect$.subscribe();
  }

  async ngOnInit(): Promise<void> {
    this.initUserEffect$.subscribe();
  }

  // only checks if fields should be disabled by write permission
  // because data should not get here without read permission
  updateReadOnly() {
    const canUpdateDispoUsers = this.permissionsService.canUpdateDispoUsers();
    // check if this.loggedInUser can edit this.profileUser
    if (this.loggedInUser?._id === this.profileUser?._id) {
      if (this.loggedInUser?.role === RoleEnum.DISPO) {
        if (this.dispoCustomer?.dispoNoOwnEdit) {
          this.hasEditPermission = canUpdateDispoUsers;
        } else {
          this.hasEditPermission = true;
        }
        this.isSelfDispo = true;
      } else {
        this.hasEditPermission = true;
      }
      if (
        this.loggedInUser?.role === RoleEnum.POOL &&
        this.loggedInUser?.customerInformation
      ) {
        Object.keys(this.loggedInUser.customerInformation).forEach((key) => {
          if (
            this.loggedInUser?.role === RoleEnum.POOL &&
            this.loggedInUser.customerInformation?.[key].hourlyWage
          ) {
            this.poolerHourlyWages.push({
              wage: this.loggedInUser.customerInformation[key].hourlyWage!,
              customer: this.loggedInUser.customerInformation[key].customerName,
            });
          }
          if (
            this.loggedInUser?.role === RoleEnum.POOL &&
            this.loggedInUser.customerInformation?.[key].monthlyHours
          ) {
            this.poolerMonthlyHours.push({
              hours: this.loggedInUser.customerInformation[key].monthlyHours!,
              customer: this.loggedInUser.customerInformation[key].customerName,
            });
          }
        });
      }
      this.resolvePoolerCustomCriteriaForSelfProfile();
      return;
    }
    if (
      this.loggedInUser?.role === RoleEnum.DISPO &&
      this.loggedInUser.customerId
    ) {
      if (this.profileUser?.role === RoleEnum.DISPO) {
        this.hasEditPermission = this.permissionsService.canUpdateDispoUsers();
        return;
      } else if (this.profileUser?.role === RoleEnum.REQUESTER) {
        this.hasEditPermission =
          this.permissionsService.canUpdateRequesterUsers();
        return;
      } else if (this.profileUser?.role === RoleEnum.POOL) {
        this.customerInformation = this.profileUser?.customerInformation?.[
          this.loggedInUser.customerId
        ] || { customerName: this.dispoCustomer?.name || '' };
        if (
          this.dispoCustomer?.enableJobs &&
          this.dispoCustomer?.enableEmployments &&
          this.customerInformation.enableJobs === undefined
        ) {
          this.customerInformation.enableJobs = true;
        } else if (
          !this.dispoCustomer?.enableJobs &&
          this.dispoCustomer?.enableEmployments
        ) {
          this.customerInformation.enableEmployments = true;
        }
        this.mergeCustomerCustomCriteriaIntoInfo(
          this.customerInformation,
          this.dispoCustomer,
        );
        this.hasEditPermission = this.permissionsService.canUpdatePoolerUsers();
      }
    } else {
      // Pooler never gets read only view of a profile
      this.hasEditPermission = true;
    }
  }

  private mergeCustomerCustomCriteriaIntoInfo(
    info: UserCustomerInformation,
    customer: Customer | undefined,
  ): void {
    if (!customer) {
      return;
    }
    const customerCustomCriterias = customer.customCriteria;
    if (!customerCustomCriterias?.length) {
      info.customCriteria = undefined;
      return;
    }

    const existingCustomCriterias = info.customCriteria || {};
    const newCustomCriteria: NonNullable<
      UserCustomerInformation['customCriteria']
    > = {};

    customerCustomCriterias.forEach((customerCustomCriteria) => {
      const existingCustomCriteria =
        existingCustomCriterias[customerCustomCriteria.id];
      if (customerCustomCriteria.type === CustomCriteriaType.TOGGLE) {
        if (existingCustomCriteria?.type === CustomCriteriaType.TOGGLE) {
          newCustomCriteria[customerCustomCriteria.id] = {
            id: customerCustomCriteria.id,
            displayName: customerCustomCriteria.displayName,
            type: CustomCriteriaType.TOGGLE,
            value: existingCustomCriteria.value,
          };
        } else {
          newCustomCriteria[customerCustomCriteria.id] = {
            id: customerCustomCriteria.id,
            displayName: customerCustomCriteria.displayName,
            type: CustomCriteriaType.TOGGLE,
            value: customerCustomCriteria.defaultValueOnProfile,
          };
        }
      } else {
        if (existingCustomCriteria?.type === CustomCriteriaType.SELECT) {
          newCustomCriteria[customerCustomCriteria.id] = {
            id: customerCustomCriteria.id,
            displayName: customerCustomCriteria.displayName,
            type: CustomCriteriaType.SELECT,
            values: existingCustomCriteria.values,
          };
        } else {
          newCustomCriteria[customerCustomCriteria.id] = {
            id: customerCustomCriteria.id,
            displayName: customerCustomCriteria.displayName,
            type: CustomCriteriaType.SELECT,
            values: [],
          };
        }
      }
    });

    info.customCriteria = newCustomCriteria;
  }

  get customCriteriaDefinitionsForDisplay(): CustomCriteria[] {
    if (this.loggedInUser?.role === RoleEnum.DISPO && this.dispoCustomer) {
      return this.dispoCustomer.customCriteria ?? [];
    }
    if (this.poolerCustomers?.length) {
      return this.mergeCustomCriteriaDefinitionsFromCustomers(
        this.poolerCustomers,
      );
    }
    return this.poolerProfileCustomerForCriteria?.customCriteria ?? [];
  }

  private mergeCustomCriteriaDefinitionsFromCustomers(
    customers: Customer[],
  ): CustomCriteria[] {
    const byId = new Map<string, CustomCriteria>();
    for (const c of customers) {
      for (const def of c.customCriteria || []) {
        if (!byId.has(def.id)) {
          byId.set(def.id, def);
        }
      }
    }
    return Array.from(byId.values());
  }

  private mergeAllPoolerCustomersCustomCriteriaIntoInfo(
    info: UserCustomerInformation,
    customers: Customer[],
    profileUser: PoolerUser,
  ): void {
    const merged: NonNullable<UserCustomerInformation['customCriteria']> = {};
    for (const customer of customers) {
      if (!customer.customCriteria?.length) {
        continue;
      }
      const slice: UserCustomerInformation = profileUser.customerInformation?.[
        customer._id
      ] || {
        customerName: customer.name,
      };
      this.mergeCustomerCustomCriteriaIntoInfo(slice, customer);
      if (slice.customCriteria) {
        Object.assign(merged, slice.customCriteria);
      }
    }
    info.customCriteria = Object.keys(merged).length ? merged : undefined;
  }

  private resolvePoolerCustomCriteriaForSelfProfile(): void {
    if (
      this.loggedInUser?.role !== RoleEnum.POOL ||
      this.profileUser?.role !== RoleEnum.POOL ||
      this.loggedInUser?._id !== this.profileUser?._id
    ) {
      this.poolerProfileCustomerForCriteria = undefined;
      return;
    }
    if (!this.poolerCustomers?.length || !this.profileUser) {
      return;
    }
    const primaryCustomer = this.poolerCustomers[0];
    this.poolerProfileCustomerForCriteria = primaryCustomer;
    this.customerInformation = this.profileUser.customerInformation?.[
      primaryCustomer._id
    ] || {
      customerName: primaryCustomer.name,
    };
    if (this.poolerCustomers.length === 1) {
      this.mergeCustomerCustomCriteriaIntoInfo(
        this.customerInformation,
        primaryCustomer,
      );
    } else {
      this.mergeAllPoolerCustomersCustomCriteriaIntoInfo(
        this.customerInformation,
        this.poolerCustomers,
        this.profileUser,
      );
    }
  }

  trackByCustomCriteriaId(_index: number, def: CustomCriteria): string {
    return def.id;
  }

  profileToggleCriterion(criteriaId: string): CustomCriteriaOnJobToggle {
    return this.customerInformation!.customCriteria![
      criteriaId
    ] as CustomCriteriaOnJobToggle;
  }

  profileSelectCriterion(criteriaId: string): CustomCriteriaOnJobSelect {
    return this.customerInformation!.customCriteria![
      criteriaId
    ] as CustomCriteriaOnJobSelect;
  }

  getSelectOptionsForCustomCriteria(
    def: CustomCriteria,
  ): { id: string; displayName: string }[] {
    return def.type === CustomCriteriaType.SELECT ? def.options : [];
  }

  touchCustomCriteriaInCustomerInformation(): void {
    if (!this.customerInformation?.customCriteria) return;
    this.customerInformation = {
      ...this.customerInformation,
      customCriteria: { ...this.customerInformation.customCriteria },
    };
  }

  /** When false, poolers may not change this value on their own profile (dispo can still edit). */
  isCustomCriterionEditableByPooler(def: CustomCriteria): boolean {
    return def.userEditable !== false;
  }

  isCustomCriteriaControlDisabled(def: CustomCriteria): boolean {
    if (!this.hasEditPermission) {
      return true;
    }
    const poolerOnOwnProfile =
      this.loggedInUser?.role === RoleEnum.POOL &&
      this.loggedInUser?._id === this.profileUser?._id;
    if (poolerOnOwnProfile && !this.isCustomCriterionEditableByPooler(def)) {
      return true;
    }
    return false;
  }

  updatePermissions(permissions: DispoPermissions) {
    if (
      this.profileUser?.role !== RoleEnum.DISPO ||
      !permissions ||
      !this.form
    ) {
      return;
    }
    this.profileUser.dispoPermissions = this.profileUser.dispoPermissions || {};
    // pooler
    this.profileUser.dispoPermissions.poolerAccess = permissions.poolerAccess;
    // department lead
    this.profileUser.dispoPermissions.requesterAccess =
      permissions.requesterAccess;

    if (!this.profileUser.dispoPermissions.dispoAccess) {
      this.profileUser.dispoPermissions.dispoAccess = {};
    }
    // dispo
    this.profileUser.dispoPermissions.dispoAccess.update =
      permissions.dispoAccess?.update || false;
    this.profileUser.dispoPermissions.dispoAccess.create =
      permissions.dispoAccess?.create || false;
    this.profileUser.dispoPermissions.dispoAccess.read =
      permissions.dispoAccess?.read || false;
    this.profileUser.dispoPermissions.dispoAccess.delete =
      permissions.dispoAccess?.delete || false;
    if (permissions.dispoAccess?.featurePermissions) {
      this.updateFeaturePermissions(permissions.dispoAccess);
    }
    this.form.control.markAsDirty();
  }

  updateFeaturePermissions(
    dispoPermission: Pick<DispoPermission, 'featurePermissions'>,
  ) {
    if (this.profileUser?.role !== RoleEnum.DISPO || !this.form) {
      return;
    }
    this.profileUser.dispoPermissions = this.profileUser.dispoPermissions || {};
    if (!this.profileUser.dispoPermissions.dispoAccess) {
      this.profileUser.dispoPermissions.dispoAccess = {};
    }
    this.profileUser.dispoPermissions.dispoAccess.featurePermissions =
      dispoPermission.featurePermissions;
    this.form.control.markAsDirty();
  }

  setLoggedInUser(user: User) {
    this.ngZone.run(() => {
      if (user) {
        this.loggedInUser = user;
      }
    });
  }

  filesTracker: Tracker.Computation | undefined;

  private _initUser(
    userId: string | undefined,
    currentUser: User,
    departments: Department[],
    isDemo: boolean,
  ) {
    this.userId = userId;
    let email = this.route.snapshot.queryParams['email'];
    if (this.userId) {
      this.initUserTracker?.stop();
      this.initUserTracker = Tracker.autorun(
        (computation: Tracker.Computation) => {
          this.ngZone.run(() => {
            let editor = Users.findOne(Meteor.userId()) as DispoUser;
            if (editor && editor.customerId) {
              this.editor = editor;
              if (
                email &&
                this.userId == 'new' &&
                editor?.role === RoleEnum.DISPO &&
                this.editor.customerId
              ) {
                if (currentUser.role !== RoleEnum.DISPO) {
                  throw new Error(
                    'cant create users without having dispo role',
                  );
                }
                const newUserRole = this.route.snapshot.queryParams[
                  'isTargetDispo'
                ]
                  ? RoleEnum.DISPO
                  : this.route.snapshot.queryParams['isTargetRequester']
                  ? RoleEnum.REQUESTER
                  : RoleEnum.POOL;
                this.isDispoEditingPooler = newUserRole === RoleEnum.POOL;

                this.editedUserRole = newUserRole;

                //creating new user
                this.isNewUser = true;
                this.profileUser = this._getNewUser(
                  email,
                  newUserRole,
                  currentUser,
                  isDemo,
                );
                this.profileUser$.next(this.profileUser);

                this.updateReadOnly();
                this.oldEmail = this.profileUser.email;
                this.oldPhone = this.profileUser.phone;

                let writableDepartmentIds = departments.map(
                  (department) => department.id,
                );
                const dispoPermissions = makePermissionsComplete(
                  editor.dispoPermissions,
                );
                const departmentPermissions = dispoPermissions.departments;

                if (!departmentPermissions.readAll) {
                  const permissionWriteDepartmentIds = getWriteDepartmentIds(
                    departmentPermissions,
                  );
                  writableDepartmentIds = writableDepartmentIds.filter(
                    (departmentId) =>
                      permissionWriteDepartmentIds.includes(departmentId),
                  );
                }
                switch (this.profileUser.role) {
                  case RoleEnum.POOL:
                    const customer = Customers.findOne(this.editor.customerId);
                    if (!this.profileUser.customerInformation) {
                      this.profileUser.customerInformation = {};
                    }
                    this.profileUser.customerInformation[
                      this.editor.customerId
                    ] = {
                      customerName: customer.name,
                      departmentIds: writableDepartmentIds,
                    };
                    this.mergeCustomerCustomCriteriaIntoInfo(
                      this.profileUser.customerInformation[
                        this.editor.customerId
                      ],
                      customer,
                    );
                    break;
                  case RoleEnum.DISPO:
                  case RoleEnum.REQUESTER:
                    let departmentPermissions =
                      this.profileUser.role === RoleEnum.REQUESTER
                        ? this.profileUser.departmentAccess
                        : this.profileUser.dispoPermissions.departments;
                    if (!departmentPermissions) {
                      departmentPermissions = {};
                    }
                    departmentPermissions.specificDepartmentIds =
                      writableDepartmentIds.reduce(
                        (
                          acc: NonNullable<
                            DepartmentPermissions['specificDepartmentIds']
                          >,
                          departmentId,
                        ) => {
                          acc[departmentId] = {
                            read: true,
                            write: true,
                          };
                          return acc;
                        },
                        {},
                      );
                    if (this.profileUser.role === RoleEnum.REQUESTER) {
                      this.profileUser.departmentAccess = departmentPermissions;
                    } else {
                      this.profileUser.dispoPermissions.departments =
                        departmentPermissions;
                    }
                    break;
                }
                computation.stop();
              } else {
                //editing user
                // findOne tracker is not working (flaky) when not waiting for users subscription ready
                this.subscriptionService.usersOfCustomerReady$
                  .pipe(takeUntil(this.destroyed$))
                  .subscribe(() => {
                    if (this.userId) {
                      this.setProfileUserTracker(this.userId);
                    }
                  });
              }
            }
          });
        },
      );
    } else {
      //Normal profile edit
      this.documentUploadsSubscription = MeteorObservable.subscribe(
        'documentUploads',
      ).subscribe(() => {
        if (this.filesTracker) {
          this.filesTracker.stop();
        }
        this.filesTracker = Tracker.autorun(() => {
          this.ngZone.run(() => {
            this.uploadedFiles = DocumentUploads.find({
              userId: Meteor.userId(),
            }).fetch();
          });
        });
      });
      this.subscriptionService.myProfile.ready$
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => {
          this.ownUserTracker = Tracker.autorun(() => {
            let ownUser = Users.findOne(Meteor.userId());
            if (!ownUser) {
              return;
            }
            this.ngZone.run(() => {
              this.userOptInsSubscription = MeteorObservable.subscribe(
                'userOptIns',
                ownUser._id,
              ).subscribe();
              this.userOptIns = OptIns.find({
                userId: ownUser._id,
              }).fetch();
            });
            this.setProfileUser(ownUser);
          });
        });
    }
  }

  private setProfileUser(user: User) {
    this.ngZone.run(() => {
      if (user) {
        if (user.role === RoleEnum.POOL) {
          this.showDocumentsSubmitted = user.documentsSubmitted || false;
        }
        this.profileUser = user;
        this.profileUser$.next(this.profileUser);
        this.updateReadOnly();

        this.oldEmail = this.profileUser.email;
        this.oldPhone = this.profileUser.phone;

        if (
          this.profileUser.role === RoleEnum.POOL &&
          !this.profileUser.placeOfResidence
        ) {
          this.profileUser.placeOfResidence = {
            zip: '',
            location: '',
          };
        }

        if (!this.profileUser.phone) {
          this.profileUser.phone = '';
        }
        if (this.loggedInUser?.role !== RoleEnum.POOL) {
          if (
            this.isDispoEditingPooler &&
            this.profileUser.role === RoleEnum.POOL &&
            this.editor?.role === RoleEnum.DISPO &&
            this.editor.customerId
          ) {
            if (
              this.profileUser.customerInformation?.[this.editor.customerId]
            ) {
              if (!this.joinDate) {
                this.joinDate =
                  this.profileUser.customerInformation[
                    this.editor.customerId
                  ].joinDate;
              }
              if (!this.leaveDate) {
                this.leaveDate =
                  this.profileUser.customerInformation[
                    this.editor.customerId
                  ].leaveDate;
              }
              if (!this.tags.length) {
                this.tags =
                  this.profileUser.customerInformation[this.editor.customerId]
                    .tags || [];
              }
            }
          }
        }

        if (this.loggedInUser?.role === RoleEnum.POOL) {
          //Pooler profile view
        } else {
          this.ccEmails = this.profileUser.ccEmails
            ? this.profileUser.ccEmails.join(', ')
            : '';
        }
      }
    });
  }

  setProfileUserTracker(userId: string) {
    if (this.profileUserTrackerId === userId) {
      return;
    }
    this.profileUserTrackerId = userId;
    if (this.profileUserTracker) {
      this.profileUserTracker.stop();
    }
    this.profileUserTracker = Tracker.autorun(() => {
      let user = Users.findOne(userId);
      if (!user) {
        // tracker will re-run once user is there
        return;
      }
      this.setProfileUser(user);
    });
  }

  private _getNewUser(
    email: string,
    userRole: RoleEnum = RoleEnum.POOL,
    currentUser: DispoUser,
    isDemo: boolean,
  ): User {
    let baseUser: BaseUser = {
      _id: '',
      emails: [{ address: email, verified: false }],
      firstName: '',
      lastName: '',
      email: email,
      phone: '+41',
      // by default notifySms is enabled for prod and disabled for demo
      notifySms: !isDemo,
      notifyEmail: true,
      phoneConfirmed: true,
      emailConfirmed: true,
      role: userRole,
      sex: SexEnum.FEMALE,
    };
    let user: User;
    if (userRole === RoleEnum.DISPO) {
      user = baseUser as DispoUser;
      // Use default permissions but take department settings from current user
      user.dispoPermissions = getDefaultDispoPermissions();
      user.dispoPermissions.departments = cloneDeep(
        currentUser.dispoPermissions?.departments,
      );
    } else if (userRole === RoleEnum.REQUESTER) {
      user = baseUser as RequesterUser;
      user.departmentAccess = cloneDeep(
        currentUser.dispoPermissions?.departments,
      );
    } else {
      user = baseUser as PoolerUser;
      user.shifts = [
        ShiftEnum.EARLY,
        ShiftEnum.DAY,
        ShiftEnum.LATE,
        ShiftEnum.NIGHT,
      ];
      user.jobTypes = [
        JobTypeEnum.NORMAL,
        JobTypeEnum.ONE_TO_ONE,
        JobTypeEnum.PATIENT_SITTING,
      ];
    }
    return user;
  }

  certificateSelectionChanged(certificates: Certificate[]) {
    if (this.profileUser?.role !== RoleEnum.POOL || !this.form) {
      return;
    }
    this.profileUser.certificates = certificates.map((certificate) => {
      return { id: certificate._id, displayName: certificate.name };
    });
    this.form.control.markAsDirty();
  }

  specialitySelectionChanged(specialities: Speciality[]) {
    if (this.profileUser?.role !== RoleEnum.POOL || !this.form) {
      return;
    }
    this.profileUser.specialities = specialities.map((speciality) => {
      return { id: speciality._id, displayName: speciality.name };
    });
    this.form.control.markAsDirty();
  }

  removeLanguage(id: LanguageEnum): void {
    if (
      this.profileUser?.role !== RoleEnum.POOL ||
      !this.languageInput?.control ||
      !this.profileUser.languages
    ) {
      return;
    }
    let index = this.profileUser.languages?.findIndex(
      (language) => language == id,
    );
    if (index === -1) return;
    this.profileUser.languages.splice(index, 1);
    this.languageInput.control.markAsDirty();
  }

  selectedLanguage(event: MatAutocompleteSelectedEvent): void {
    if (
      this.profileUser?.role !== RoleEnum.POOL ||
      !this.languageInput?.control
    ) {
      return;
    }
    if (
      !this.profileUser.languages ||
      !this.profileUser.languages.includes(event.option.value)
    ) {
      if (!this.profileUser.languages) {
        this.profileUser.languages = [];
      }
      this.profileUser.languages.push(event.option.value);
      this.languageInput.control.markAsDirty();
    }
    // @ts-ignore
    this.languageInput.control.nativeElement.blur();
    // @ts-ignore
    this.languageInput.control.nativeElement.value = '';
    this.languageInput.control.setValue('');
    this.languageInput.control.updateValueAndValidity();
  }

  clearJoinDate(): void {
    this.joinDate = undefined;
    this.form?.control.markAsDirty();
  }

  clearLeaveDate(): void {
    this.leaveDate = undefined;
    this.form?.control.markAsDirty();
  }

  hasChild = (_: number, node: Organisation) =>
    !!node.departments && node.departments.length > 0;

  ngOnDestroy(): void {
    if (this.isNewUser && this.temporaryCvFiles.length > 0) {
      this.temporaryCvFiles.forEach((file) => {
        Meteor.call('removeInviteeFile', file, () => {});
      });
    }
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.userOptInsSubscription) {
      this.userOptInsSubscription.unsubscribe();
    }
    if (this.profileUserTracker) {
      this.profileUserTracker.stop();
    }
    if (this.ownUserTracker) {
      this.ownUserTracker.stop();
    }
    if (this.initUserTracker) {
      this.initUserTracker.stop();
    }
    if (this.customerTracker) {
      this.customerTracker.stop();
    }
    if (this.documentUploadsSubscription) {
      this.documentUploadsSubscription.unsubscribe();
    }
    if (this.filesTracker) {
      this.filesTracker.stop();
    }
  }

  onTemporaryFilesChange(files: InviteeFile[]) {
    this.temporaryCvFiles = files;
  }

  historyBack() {
    this.location.back();
  }

  trackByIndex(index: number): any {
    return index;
  }

  get getPhoneOptIn(): OptIn | undefined {
    return this.userOptIns.find((optIn) => optIn.type === 'SMS');
  }

  resendSMS() {
    const phoneOptIn = this.getPhoneOptIn;
    if (phoneOptIn && this.profileUser) {
      if (this.phoneResetLockSeconds === 0) {
        this.accountService.sendPhoneConfirmationSMS(
          this.profileUser,
          phoneOptIn.value,
        );
        getCountDown(60).subscribe((res) => {
          this.phoneResetLockSeconds = res;
        });
      }
    }
  }

  get getEmailOptIn(): OptIn | undefined {
    return this.userOptIns.find((optIn) => optIn.type === 'EMAIL');
  }

  selectJoinDate(event: MatDatepickerInputEvent<any & string>) {
    this.joinDate = format(removeTimeZoneOffset(event.value), dbDayDateToken);
  }

  selectLeaveDate(event: MatDatepickerInputEvent<any & string>) {
    this.leaveDate = format(removeTimeZoneOffset(event.value), dbDayDateToken);
  }

  selectDateOfBirth(event: MatDatepickerInputEvent<any & string>) {
    if (!this.profileUser || this.profileUser.role !== RoleEnum.POOL) {
      return;
    }
    this.profileUser.dateOfBirth = format(event.value, dbDayDateToken);
  }

  clearDateOfBirth(): void {
    if (!this.profileUser || this.profileUser.role !== RoleEnum.POOL) {
      return;
    }
    this.profileUser.dateOfBirth = undefined;
    this.form?.control.markAsDirty();
  }

  selectTag(tagId: string) {
    if (!this.dispoCustomer?.tags) {
      return;
    }
    const tag = this.dispoCustomer.tags.find((tag) => tag.id === tagId);
    if (!tag) {
      return;
    }
    this.tags.push(tag);
    this.form?.control.markAsDirty();
  }

  removeTag(tagId: string) {
    this.tags.splice(
      this.tags.findIndex((tag) => tag.id === tagId),
      1,
    );
    this.form?.control.markAsDirty();
  }

  addNewlyCreatedTag(tag: Tag) {
    this.tags.push(tag);
    this.form?.control.markAsDirty();
  }

  get showBlacklistDepartments(): boolean {
    return (
      this.loggedInUser?.role === RoleEnum.POOL &&
      this.profileUser?.role === RoleEnum.POOL
    );
  }

  async saveProfile() {
    if (!this.form?.valid) {
      this.form?.control.markAsDirty();
      this.form?.control.markAllAsTouched();
      this.toastService.add({ type: 'INFO', message: 'InvalidForm' });
      return;
    }

    // Check if pooler user has empty jobTypes or shifts
    if (this.profileUser?.role === RoleEnum.POOL) {
      if (this.profileUser.jobTypes && this.profileUser.jobTypes.length === 0) {
        this.form?.control.markAsDirty();
        this.form?.control.markAllAsTouched();
        this.toastService.add({ type: 'ERROR', message: 'jobTypesRequired' });
        return;
      }
      if (this.profileUser.shifts && this.profileUser.shifts.length === 0) {
        this.form?.control.markAsDirty();
        this.form?.control.markAllAsTouched();
        this.toastService.add({ type: 'ERROR', message: 'shiftsRequired' });
        return;
      }
    }

    if (this.profileUser?.phone === '' || this.profileUser?.phone === '+41') {
      delete this.profileUser.phone;
      if (this.profileUser?.twoFactorAuthEnabled) {
        this.form.control.markAllAsTouched();
        return;
      }
    }
    if (!this.profileUser) {
      return;
    }
    this.profileUser.email = this.profileUser.email.toLowerCase();

    let baseUser: Omit<BaseUser, 'role'> = {
      _id: this.profileUser._id,
      sex: this.profileUser.sex,
      firstName: this.profileUser.firstName,
      lastName: this.profileUser.lastName,
      email: this.profileUser.email,
      phone: this.profileUser.phone,
      notifyEmail: this.profileUser.notifyEmail,
      notifySms: this.profileUser.notifySms,
      locale: this.profileUser.locale,
    };

    let user: DispoUser | PoolerUser | RequesterUser;

    if (this.profileUser.role === RoleEnum.POOL) {
      user = {
        ...baseUser,
        role: this.profileUser.role,
        shifts: this.profileUser.shifts,
        jobTypes: this.profileUser.jobTypes,
        certificates: this.profileUser.certificates,
        specialities: this.profileUser.specialities,
        availabilities: this.profileUser.availabilities,
        car: this.profileUser.car,
        itKnowledge: this.profileUser.itKnowledge,
        clothingSize: this.profileUser.clothingSize,
        height: this.profileUser.height,
        dateOfBirth: this.profileUser.dateOfBirth,
        placeOfResidence: this.profileUser.placeOfResidence,
        languages: this.profileUser.languages,
        degrees: this.profileUser.degrees,
        twoFactorAuthEnabled: this.profileUser.twoFactorAuthEnabled,
      } as PoolerUser;
      if (
        this.loggedInUser?.role === RoleEnum.POOL &&
        this.loggedInUser?._id === this.profileUser._id &&
        this.poolerCustomers?.length &&
        this.customerInformation
      ) {
        user.customerInformation = {
          ...(this.profileUser.customerInformation || {}),
        };
        if (this.poolerCustomers.length === 1) {
          user.customerInformation[this.poolerCustomers[0]._id] =
            this.customerInformation;
        } else {
          for (const c of this.poolerCustomers) {
            const prev = this.profileUser.customerInformation?.[c._id] || {
              customerName: c.name,
            };
            const defs = c.customCriteria;
            if (!defs?.length) {
              user.customerInformation[c._id] = { ...prev };
              continue;
            }
            const idSet = new Set(defs.map((d) => d.id));
            const picked: NonNullable<
              UserCustomerInformation['customCriteria']
            > = {};
            if (this.customerInformation.customCriteria) {
              for (const id of idSet) {
                const value = this.customerInformation.customCriteria[id];
                if (value !== undefined) {
                  picked[id] = value;
                }
              }
            }
            user.customerInformation[c._id] = {
              ...prev,
              customCriteria:
                Object.keys(picked).length > 0 ? picked : prev.customCriteria,
            };
          }
        }
      }
    } else if (this.profileUser.role === RoleEnum.DISPO) {
      user = {
        ...baseUser,
        role: RoleEnum.DISPO,
        disabledDispoEmails: this.profileUser.disabledDispoEmails,
        dispoPermissions: this.profileUser.dispoPermissions || {},
        branch: this.profileUser.branch,
      } as DispoUser;

      if (this.loggedInUser?._id === this.profileUser._id) {
        user.disableUnsavedChangesWarning =
          this.profileUser.disableUnsavedChangesWarning;
      }
    } else if (this.profileUser.role === RoleEnum.REQUESTER) {
      user = {
        ...baseUser,
        role: RoleEnum.REQUESTER,
        departmentAccess: this.profileUser.departmentAccess,
        branch: this.profileUser.branch,
      } as RequesterUser;
    } else {
      return;
    }
    if (
      user.role === RoleEnum.POOL &&
      this.loggedInUser?.role === RoleEnum.DISPO &&
      this.loggedInUser.customerId &&
      this.profileUser.role === RoleEnum.POOL
    ) {
      if (this.customerInformation) {
        user.customerInformation = {
          [this.loggedInUser.customerId]: this.customerInformation,
        };
      } else {
        const customer = Customers.findOne({
          _id: this.loggedInUser.customerId,
        });
        user.customerInformation = {
          [this.loggedInUser.customerId]: {
            customerName: customer.name,
            internalNote: this.newNote,
            hourlyWage: this.newHourlyWage,
            monthlyHours: this.newMonthlyHours,
            internalId: this.newInternalId,
            perigonId: this.newPerigonId,
          },
        };
      }
      user.customerInformation[this.loggedInUser.customerId].joinDate = this
        .joinDate
        ? this.joinDate
        : undefined;
      user.customerInformation[this.loggedInUser.customerId].leaveDate = this
        .leaveDate
        ? this.leaveDate
        : undefined;
      user.customerInformation[this.loggedInUser.customerId].tags = this.tags
        .length
        ? this.tags
        : undefined;
      user.customerInformation[this.loggedInUser.customerId].departmentIds =
        this.profileUser.customerInformation?.[
          this.loggedInUser.customerId
        ].departmentIds;
    }
    if (this.ccEmails) {
      user.ccEmails = this.ccEmails
        .toLowerCase()
        .replace(' ', '')
        .split(',')
        .filter((ccEmail) => ccEmail);
    } else {
      user.ccEmails = [];
    }
    if (
      (this.editedUserRole === RoleEnum.DISPO || this.isSelfDispo) &&
      user.role === RoleEnum.DISPO &&
      this.profileUser.role === RoleEnum.DISPO
    ) {
      user.getOwnJobConfirmations = this.profileUser.getOwnJobConfirmations;
    }
    if (this.isNewUser) {
      if (
        this.editedUserRole === RoleEnum.DISPO &&
        user.role === RoleEnum.DISPO &&
        this.profileUser.role === RoleEnum.DISPO
      ) {
        user.dispoPermissions = this.profileUser.dispoPermissions || {};
        // user.dispoPermissions.departments = this.selectedDepartments;
      } else if (
        user.role === RoleEnum.POOL &&
        this.loggedInUser?.role === RoleEnum.DISPO &&
        this.loggedInUser.customerId
      ) {
        if (!user.customerInformation) {
          user.customerInformation = {};
        }
        if (this.customerInformation) {
          user.customerInformation = {
            [this.loggedInUser.customerId]: this.customerInformation,
          };
        }
        user.customerInformation[this.loggedInUser.customerId].joinDate = this
          .joinDate
          ? this.joinDate
          : undefined;
        user.customerInformation[this.loggedInUser.customerId].leaveDate = this
          .leaveDate
          ? this.leaveDate
          : undefined;
        user.customerInformation[this.loggedInUser.customerId].tags = this.tags
          .length
          ? this.tags
          : undefined;
      } else if (user.role === RoleEnum.REQUESTER) {
        // user.departmentAccess = this.selectedDepartments;
      }

      if (this.oldEmail != this.profileUser.email && this.oldEmail) {
        this.toastService.add({
          type: 'ERROR',
          message: 'changeEmailNotAllowedForNewUser',
          timeout: 10000,
        });
        this.profileUser.email = this.oldEmail;
        return;
      }
      if (this.dispoCustomer?.requireTwoFactorAuth) {
        user.twoFactorAuthRequired = true;
        user.twoFactorAuthEnabled = true;
      }
      user.emails = this.profileUser.emails;
      Meteor.apply(
        'createNewUser',
        [user, this.unsavedDepartmentChanges, this.temporaryCvFiles],
        {
          wait: false,
          onResultReceived: (error, result: any) => {
            if (error) {
              if (isInputValidationError(error)) {
                logInputValidationError({
                  error,
                  input: {
                    user,
                    departmentSettingChanges: this.unsavedDepartmentChanges,
                    temporaryFiles: this.temporaryCvFiles,
                  },
                });
              }
              this.toastService.makeServerErrorToast();
              return;
            }
            this.form?.control.markAsPristine();
            this.toastService.add({ type: 'SUCCESS', message: 'userCreated' });
            this.location.back();
          },
        },
      );
    } else {
      if (this.oldEmail != this.profileUser.email) {
        if (this.profileUser.emailConfirmed !== undefined) {
          this.accountService.sendEmailConfirmation(this.profileUser);
          this.ngZone.run(() => {
            user.email = this.oldEmail || '';
            if (!this.profileUser) {
              return;
            }
            this.profileUser.email = this.oldEmail || '';
          });
        } else {
          if (this.editor?.role === RoleEnum.DISPO && this.editor?.customerId) {
            await this.accountService
              .sendEnrollEmail(this.profileUser, this.editor.customerId)
              .catch((err) => {
                console.error(err);
                user.email = this.oldEmail || '';
                if (!this.profileUser) {
                  return;
                }
                this.profileUser.email = this.oldEmail || '';
              });
          }
        }
      }

      if (this.oldPhone != this.profileUser.phone) {
        if (
          this.profileUser.phoneConfirmed !== undefined &&
          this.profileUser.phone
        ) {
          this.accountService.sendPhoneConfirmationSMS(
            this.profileUser,
            this.profileUser.phone,
          );
          this.ngZone.run(() => {
            user.phone = this.oldPhone;
            if (!this.profileUser) {
              return;
            }
            this.profileUser.phone = this.oldPhone;
          });
        } else {
          const oldPhone = this.oldPhone;
          const revertPhone = () => {
            user.phone = oldPhone;
            if (!this.profileUser) {
              return;
            }
            this.profileUser.phone = oldPhone;
          };
          await this.accountService
            .sendEnrollSMS(this.profileUser)
            .catch((err) => {
              console.error(err);
              revertPhone();
            });
        }
      }
      const updateProfileInput: UpdateProfileArgs = {
        ...user,
        _id: this.profileUser._id,
        departmentSettingChanges: this.unsavedDepartmentChanges,
      };
      Meteor.call(
        'updateProfile',
        updateProfileInput,
        (error: Meteor.Error) => {
          if (error?.error) {
            if (isInputValidationError(error)) {
              logInputValidationError({
                error,
                input: updateProfileInput,
              });
            }
            this.toastService.add({
              type: 'ERROR',
              message:
                typeof error.error === 'string' ? error.error : 'serverError',
            });
            return;
          }
          this.form?.control.markAsPristine();
          this.toastService.add({ type: 'SUCCESS', message: 'profileSaved' });
          if (this.isDispoEditingPooler) {
            this.historyBack();
          }
        },
      );
    }
  }

  get showDepartments(): boolean {
    return (
      (this.profileUser &&
        this.loggedInUser?.role === RoleEnum.POOL &&
        this.poolerCustomers?.some(
          (customer) => customer.enableUserPreferenceDepartmentBlacklist,
        )) ||
      (this.loggedInUser?.role !== RoleEnum.POOL &&
        this.route.snapshot.queryParams['userId'])
    );
  }

  get multiselectModeForRole(): DepartmentMultiSelectMode | undefined {
    const loggedInUserRole = this.loggedInUser?.role;
    const profileUserRole = this.profileUser?.role;
    if (loggedInUserRole === undefined || profileUserRole === undefined) {
      return undefined;
    }

    return departmentMultiselectModeForRole[loggedInUserRole]?.[
      profileUserRole
    ];
  }

  selectedDepartmentIds(departmentChanges: DepartmentChangesDto) {
    this.unsavedDepartmentChanges = departmentChanges;
    if (Object.keys(departmentChanges.selection).length > 0) {
      this.form?.control.markAsDirty();
    }
  }

  trimPhone() {
    if (this.profileUser?.phone) {
      this.profileUser.phone = trimPhone(this.profileUser.phone);
    }
  }

  poolerTypeChanged(poolerTypeId: string) {
    const poolerType = this.dispoCustomer?.poolerTypes?.find(
      (type) => type.id === poolerTypeId,
    );
    if (!this.customerInformation) {
      return;
    }
    this.customerInformation.poolerType = poolerType;
    this.form?.control.markAsDirty();
  }

  changePassword() {
    if (!this.profileUser?.emails?.[0]?.address) {
      return;
    }
    Accounts.forgotPassword(
      { email: this.profileUser.emails[0].address },
      (error) => {
        if (error) {
          this.toastService.makeServerErrorToast();
          return;
        }
        this.toastService.add({
          type: 'SUCCESS',
          message: 'passwordResetEmailSent',
        });
      },
    );
  }

  addDegree(event: Event) {
    event.stopPropagation();
    if (this.profileUser?.role === RoleEnum.POOL && !this.profileUser.degrees) {
      this.profileUser.degrees = [];
    }
    this.ngZone.run(() => {
      if (this.profileUser?.role === RoleEnum.POOL) {
        if (!this.profileUser.degrees) {
          this.profileUser.degrees = [];
        }
        this.profileUser.degrees.push('');
      }
    });
  }

  submitDocuments(event: Event) {
    event.stopPropagation();
    this.showDocumentsSubmitted = true;
    Meteor.call('notifyCustomerForActivation');
  }

  removeDegree(event: Event, index: number) {
    event.stopPropagation();
    this.ngZone.run(() => {
      if (this.profileUser?.role === RoleEnum.POOL) {
        if (!this.profileUser.degrees) {
          return;
        }
        this.profileUser.degrees.splice(index, 1);
      }
    });
    this.form?.control.markAsDirty();
  }

  downloadFile(event: Event, storedDocument: DocumentUpload) {
    const byteCharacters = atob(storedDocument.data.replace(/^[^,]+,/, ''));
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const fileObject = new File([byteArray], storedDocument.fileName, {
      type: storedDocument.type,
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(fileObject);
    link.download = fileObject.name;
    link.click();
  }

  uploadFile(event: Event) {
    const userId = this.userId || Meteor.userId();
    if (!userId) return;
    const files = (event.target as HTMLInputElement).files;
    if (!FileReader || !files || !files.length) {
      this.toastService.add({ type: 'ERROR', message: 'notSupported' });
      return;
    } else {
      Array.from(files).forEach((uploadFile) => {
        const file = uploadFile as File;
        if (file.size < 1024 * 1024 * 15) {
          const fileReader = new FileReader();
          this.isUploading = true;
          fileReader.onload = () => {
            const contents = fileReader.result;
            // @ts-ignore
            const doc: DocumentUpload = {
              userId: userId,
              type: file.type,
              size: file.size,
              fileName: file.name,
              data: contents?.toString() || '',
              timestamp: Date.now(),
            };

            DocumentUploads.insert(doc).subscribe(
              () => {
                this.toastService.add({
                  type: 'SUCCESS',
                  message: 'documentUploaded',
                });
              },
              () => {
                this.toastService.makeServerErrorToast();
              },
            );
          };
          this.isUploading = false;
          fileReader.readAsDataURL(file);
        } else {
          this.toastService.add({
            type: 'ERROR',
            message: 'maxFileUploadExceeded',
          });
        }
      });
    }
  }

  removeFileDialog(event: Event, file: DocumentUpload, templateRef: any) {
    const dialogRef = this.dialog.open(templateRef);
    dialogRef.afterClosed().subscribe((response) => {
      if (response) {
        this.removeFile(event, file);
      }
    });
  }

  removeFile(event: Event, file: DocumentUpload) {
    if (Meteor.userId() === file.userId) {
      DocumentUploads.remove({ _id: file._id }).subscribe(
        () => {
          this.toastService.add({
            type: 'SUCCESS',
            message: 'documentRemoved',
          });
        },
        () => {
          this.toastService.makeServerErrorToast();
        },
      );
    } else {
      this.toastService.add({ type: 'ERROR', message: 'notAllowed' });
    }
  }

  trackShift(index: number, shift: string) {
    return shift;
  }
  languageOptions: { language: InpoolLanguage; labelKey: string }[] = [
    {
      language: 'de',
      labelKey: 'german',
    },
    {
      language: 'en',
      labelKey: 'english',
    },
    {
      language: 'fr',
      labelKey: 'french',
    },
  ];

  CustomerTypeEnum = CustomerTypeEnum;

  hasUnsavedChanges(): boolean {
    if (this.form?.dirty) {
      return true;
    }
    return false;
  }
}
